import { motion } from "framer-motion";

const ease = [0.16, 1, 0.3, 1];

const chapters = [
  {
    num: "01",
    title: "Metodo",
    kicker: "Diagnostica",
    body:
      "Ogni percorso comincia con un'analisi profonda: postura, composizione corporea, storia, abitudini. Non prescriviamo, ascoltiamo.",
    image:
      "https://images.unsplash.com/photo-1583454110551-21f2fa2afe61?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  },
  {
    num: "02",
    title: "Percorso",
    kicker: "Programmazione",
    body:
      "Un allenamento disegnato attorno a te, non attorno a una moda. Frequenza, carichi, recupero — tarati con precisione settimana dopo settimana.",
    image: null,
  },
  {
    num: "03",
    title: "Risultato",
    kicker: "Trasformazione",
    body:
      "Ciò che rimane non è solo un corpo diverso. È una nuova relazione con la disciplina, l'energia, la costanza. Un risultato che dura.",
    image:
      "https://images.unsplash.com/photo-1550345332-09e3ac987658?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  },
];

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      data-testid="manifesto-section"
      className="relative bg-obsidian py-28 md:py-40 lg:py-48 overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Section head */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-20 md:mb-28">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease }}
            className="md:col-span-4 flex md:flex-col md:justify-between gap-6"
          >
            <span className="text-[0.7rem] uppercase tracking-[0.4em] text-gold">
              ¶ Manifesto
            </span>
            <span className="text-[0.65rem] uppercase tracking-[0.32em] text-cream/40">
              Tre capitoli · Un percorso
            </span>
          </motion.div>
          <motion.h2
            id="metodo"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.1, ease }}
            className="md:col-span-8 font-serif font-light text-cream text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.02em]"
          >
            Non vendiamo abbonamenti.
            <br />
            <em className="italic text-gold/95">Costruiamo</em> percorsi.
          </motion.h2>
        </div>

        <div className="divider-thin mb-20 md:mb-28" />

        {/* Chapters */}
        <div className="flex flex-col gap-24 md:gap-40">
          {chapters.map((ch, i) => (
            <Chapter key={ch.num} chapter={ch} reversed={i % 2 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Chapter({ chapter, reversed }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-120px" }}
      transition={{ duration: 1, ease }}
      className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 relative"
      data-testid={`chapter-${chapter.num}`}
    >
      {/* Watermark number */}
      <div
        className={`md:col-span-4 relative ${reversed ? "md:order-2" : ""}`}
      >
        <div className="chapter-num text-[8rem] md:text-[10rem] lg:text-[13rem]">
          {chapter.num}
        </div>
        <div className="mt-2 md:mt-0 md:absolute md:bottom-2 md:left-0 flex items-center gap-3">
          <span className="w-8 h-px bg-gold" />
          <span className="text-[0.68rem] uppercase tracking-[0.35em] text-gold/90">
            {chapter.kicker}
          </span>
        </div>
      </div>

      {/* Body */}
      <div
        className={`md:col-span-5 flex flex-col justify-center ${
          reversed ? "md:order-1" : ""
        }`}
      >
        <h3 className="font-serif font-light text-cream text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.01em] mb-6">
          {chapter.title}.
        </h3>
        <p className="text-cream/70 font-light text-base md:text-lg leading-relaxed max-w-md">
          {chapter.body}
        </p>
      </div>

      {/* Image */}
      <div className={`md:col-span-3 ${reversed ? "md:order-3" : ""}`}>
        {chapter.image ? (
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.4, ease }}
            className="editorial-frame aspect-[3/4] w-full grayscale-[0.35] hover:grayscale-0 transition-[filter] duration-1000"
          >
            <img
              src={chapter.image}
              alt={chapter.title}
              className="w-full h-full object-cover"
              loading="lazy"
              draggable={false}
            />
          </motion.div>
        ) : (
          <div className="editorial-frame aspect-[3/4] w-full bg-gradient-to-br from-onyx to-obsidian flex items-end p-6">
            <span className="font-serif italic text-gold/80 text-lg">— sostenibile nel tempo</span>
          </div>
        )}
      </div>
    </motion.article>
  );
}
